import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, FlatList, SafeAreaView, Text, TouchableOpacity, Alert, Platform } from 'react-native';
import { Card, Title, Paragraph, Divider, Button, ActivityIndicator, useTheme, Chip, IconButton } from 'react-native-paper';
import { getMyActivities, getActivityStatusInfo } from '../../api/activitiesAPI';
import { useFocusEffect } from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { formatDateTime, formatDuration, getMinutesBetweenDates } from '../../utils/dateUtils';
import { savePaginationState, loadPaginationState } from '../../utils/paginationUtils';

const SCREEN_KEY = 'user_park_history_screen';

const ParkHistoryScreen = ({ navigation }) => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Pagination state'ini localStorage'dan yükle
  const savedPagination = Platform.OS === 'web' ? 
    loadPaginationState(SCREEN_KEY, 1, 5) : 
    { currentPage: 1, itemsPerPage: 5 };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(savedPagination.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(savedPagination.itemsPerPage);
  
  // Pagination state değiştiğinde localStorage'a kaydet (web için)
  useEffect(() => {
    if (Platform.OS === 'web') {
      savePaginationState(SCREEN_KEY, currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage]);
  
  // Aktiviteleri getir
  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await getMyActivities();
      
      if (response.success && response.data) {
        // Oyun parkı tipi ID'sini kontrol et
        const parkActivities = response.data.filter(activity => 
          activity.activity_type_id === 1 || // Oyun Parkı ID'si
          activity.activity_type?.name?.toLowerCase().includes('oyun parkı')
        );
        
        // Tarihe göre sırala (en yeni en üstte)
        parkActivities.sort((a, b) => 
          new Date(b.start_time) - new Date(a.start_time)
        );
        
        setActivities(parkActivities);
      } else {
        Alert.alert('Hata', 'Aktivite geçmişi yüklenemedi.');
      }
    } catch (error) {
      console.error('Aktivite geçmişi yüklenirken hata:', error);
      Alert.alert('Hata', 'Aktivite geçmişi yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Sayfaya her geldiğinde aktiviteleri getir
  useFocusEffect(
    useCallback(() => {
      fetchActivities();
      return () => {};
    }, [])
  );
  
  // Aktivite süresini hesapla
  const calculateDuration = (startTime, endTime) => {
    if (!startTime) return '0 dk';
    
    if (!endTime) {
      // Devam eden aktiviteler için şu ana kadar geçen süre
      const now = new Date();
      const start = new Date(startTime);
      const minutes = Math.floor((now - start) / (1000 * 60));
      return formatDuration(minutes);
    } else {
      // Tamamlanmış aktiviteler için toplam süre
      const minutes = getMinutesBetweenDates(startTime, endTime);
      return formatDuration(minutes);
    }
  };
  
  // Dakika bazında ücret hesapla
  const calculatePrice = (activity) => {
    //console.log('Aktivite için ücret hesaplanıyor:', activity.id);
    
    if (!activity.activityType) {
      //console.log('ActivityType bulunamadı!');
      return '0.00';
    }
    
    //console.log('ActivityType değerleri:', 
     // 'base_price:', activity.activityType.base_price, 
      //'price_per_minute:', activity.activityType.price_per_minute);
    
    // API'den değerleri al (veya varsayılan değerleri kullan)
    const basePrice = parseFloat(activity.activityType.base_price || 0);
    const pricePerMinute = activity.activityType.price_per_minute !== undefined ? 
      parseFloat(activity.activityType.price_per_minute) : 3; // Varsayılan değer olarak 3 atıyoruz
    
    let minutes = 0;
    
    // İptal edilen aktiviteler için 0 TL
    if (activity.status === 'Cancelled') {
      //console.log('İptal edilmiş aktivite, ücret 0');
      return '0.00';
    }
    
    // Dakika hesapla
    if (!activity.end_time) {
      // Devam eden aktiviteler için şu ana kadar geçen süre
      const now = new Date();
      const start = new Date(activity.start_time);
      minutes = Math.floor((now - start) / (1000 * 60));
    } else {
      // Tamamlanmış aktiviteler için toplam süre
      minutes = getMinutesBetweenDates(activity.start_time, activity.end_time);
    }
    
    //console.log('Hesaplama değerleri:', 
     // 'basePrice:', basePrice, 
     // 'pricePerMinute:', pricePerMinute, 
     // 'minutes:', minutes);
    
    // Giriş ücreti + (dakika * dakika ücreti)
    const totalPrice = basePrice + (minutes * pricePerMinute);
    //console.log('Hesaplanan toplam ücret:', totalPrice);
    
    return totalPrice.toFixed(2);
  };

  // Aktivite kartını render et
  const renderActivityItem = ({ item }) => {
    const childName = item.child ? 
      `${item.child.first_name} ${item.child.last_name}` : 
      (item.child_name || 'Bilinmeyen Çocuk');
    const isActive = item.status === 'Active' && !item.end_time;
    const startTime = formatDateTime(item.start_time);
    const endTime = item.end_time ? formatDateTime(item.end_time) : null;
    const duration = calculateDuration(item.start_time, item.end_time);
    
    // Fiyat hesaplama
    const price = item.status === 'Cancelled' 
      ? '0.00' 
      : item.status === 'WaitingForApproval' && item.activityType?.base_price
        ? parseFloat(item.activityType.base_price).toFixed(2)
      : item.end_time && item.price && parseFloat(item.price) > 0 
        ? parseFloat(item.price).toFixed(2) 
        : calculatePrice(item);
    
    // Aktivite durumuna göre renk ve etiket bilgisini al
    const statusInfo = getActivityStatusInfo(item.status);
    // Mor tema için renkleri güncelle
    const statusColor = item.status === 'completed' ? 'rgb(125, 76, 236)' : item.status === 'cancelled' ? 'rgb(200, 50, 150)' : 'rgb(90, 50, 180)';
    
    return (
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerRow}>
            <Title style={styles.childName}>{childName}</Title>
            <Chip 
              mode="outlined" 
              textStyle={{ color: statusColor }}
              style={{ 
                borderColor: statusColor,
                backgroundColor: 'transparent' 
              }}
            >
              {statusInfo.label}
            </Chip>
          </View>
          
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <MaterialCommunityIcons name="clock-in" size={18} color="rgb(125, 76, 236)" />
            <Paragraph style={styles.infoText}>
              <Text style={styles.infoLabel}>Giriş: </Text>
              {startTime}
            </Paragraph>
          </View>
          
          {(item.end_time || item.status === 'Cancelled') && (
            <View style={styles.infoRow}>
              <MaterialCommunityIcons 
                name={item.status === 'Cancelled' ? "cancel" : "clock-out"} 
                size={18} 
                color={item.status === 'Cancelled' ? "#F44336" : "rgb(125, 76, 236)"} 
              />
              <Paragraph style={styles.infoText}>
                <Text style={styles.infoLabel}>
                  {item.status === 'Cancelled' ? 'İptal: ' : 'Çıkış: '}
                </Text>
                {endTime}
              </Paragraph>
            </View>
          )}
          
          <View style={styles.infoRow}>
            <MaterialCommunityIcons name="clock" size={18} color="rgb(125, 76, 236)" />
            <Paragraph style={styles.infoText}>
              <Text style={styles.infoLabel}>Süre: </Text>
              {duration}
            </Paragraph>
          </View>
          
          <View style={styles.infoRow}>
            <MaterialCommunityIcons name="calendar-clock" size={20} color="rgb(125, 76, 236)" />
            <Paragraph style={styles.infoText}>
              <Text style={styles.infoLabel}>Ücret: </Text>
              {price} TL
            </Paragraph>
          </View>
        </Card.Content>
      </Card>
    );
  };

  // Pagination hesaplamaları
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  
  // Mevcut sayfada gösterilecek aktiviteler
  const paginatedActivities = activities.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );
  
  // Önceki sayfaya geç
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Sonraki sayfaya geç
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Sayfa numaralarını göster
  const renderPagination = () => {
    // Tüm durumlarda pagination'ı göster
    return (
      <View style={styles.paginationContainer}>
        <IconButton
          icon="chevron-left"
          size={24}
          onPress={goToPreviousPage}
          disabled={currentPage === 1}
          style={[styles.paginationButton, currentPage === 1 ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
        
        <Text style={styles.paginationText}>
          Sayfa {currentPage} / {Math.max(totalPages, 1)} ({activities.length} kayıt)
        </Text>
        
        <IconButton
          icon="chevron-right"
          size={24}
          onPress={goToNextPage}
          disabled={currentPage >= totalPages}
          style={[styles.paginationButton, currentPage >= totalPages ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
      </View>
    );
  };
  
  return (
    <SafeAreaView style={styles.container}>
      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
          <Text style={styles.loadingText}>Yükleniyor...</Text>
        </View>
      ) : (
        <>
          <Card style={styles.headerCard}>
            <Card.Content>
              <Title style={styles.headerTitle}>Oyun Parkı Geçmişi</Title>
              <Paragraph style={styles.headerSubtitle}>
                Çocuklarınızın oyun parkı giriş ve çıkış kayıtları
              </Paragraph>
            </Card.Content>
          </Card>
          
          {activities.length === 0 ? (
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="cash" size={20} color="rgb(125, 76, 236)" />
              <Text style={styles.emptyText}>Henüz kayıtlı aktivite bulunmuyor</Text>
              <Button 
                mode="contained" 
                onPress={() => navigation.navigate('ParkEntry')}
                style={styles.createButton}
              >
                Oyun Parkı Girişi Yap
              </Button>
            </View>
          ) : (
            <>
              <FlatList
                data={paginatedActivities}
                renderItem={renderActivityItem}
                keyExtractor={item => item.id.toString()}
                contentContainerStyle={styles.listContainer}
                refreshing={refreshing}
                onRefresh={() => {
                  setRefreshing(true);
                  fetchActivities();
                  setCurrentPage(1); // Yenileme sırasında ilk sayfaya dön
                }}
              />
              {renderPagination()}
            </>
          )}
        </>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  headerCard: {
    margin: 16,
    marginBottom: 8,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
  },
  listContainer: {
    padding: 16,
    paddingTop: 8,
  },
  card: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  divider: {
    marginVertical: 12,
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoText: {
    marginLeft: 8,
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
  },
  infoLabel: {
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    marginTop: 16,
    marginBottom: 20,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  createButton: {
    marginTop: 8,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 16,
    marginHorizontal: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    elevation: 2,
    borderRadius: 8,
  },
  paginationButton: {
    margin: 0,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    marginHorizontal: 12,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
});

export default ParkHistoryScreen;
